export class MetaEmbeddedSignUp {
    private dotNetRef: any | null = null;
    private appId: string = '';
    private configId: string = '';
    private _initialized = false;        // Tracks SDK initialization
    private _eventListenerBound = false; // Prevent duplicate message handlers

    public init(dotNetRef: any, appId: string, configId: string): void {
        try {
            this.dotNetRef = dotNetRef;
            this.appId = appId;
            this.configId = configId;

            // Only load FB SDK once
            if (!this._initialized) {
                this._initialized = true;
                this.loadFacebookSDK();
                this.setupFBInit();
                this.defineLaunchMethod();
                console.log('MetaEmbeddedSignUp initialized');
            }

            if (!this._eventListenerBound) {
                this.initializeEventListeners();
                this._eventListenerBound = true;
                console.log('MetaEmbeddedSignUp initializeEventListeners');
            }


        } catch (e) {
            console.error('Error initializing MetaEmbeddedSignUp:', e);
        }
    }

    public dispose(): void {
        try {
            this._initialized = false;
            this.dotNetRef = null;

            console.log("MetaEmbeddedSignUp disposed");

        } catch (e) {
            console.error("Error disposing MetaEmbeddedSignUp:", e);
        }
    }

    /** Listen for messages from the Meta*/
    private initializeEventListeners(): void {
        window.addEventListener('message', async (event: MessageEvent) => {
            if (
                event.origin !== 'https://www.facebook.com' &&
                event.origin !== 'https://web.facebook.com'
            ) return;

            try {
                const data: WhatsAppSignupData = JSON.parse(event.data);
                if (data.type === 'WA_EMBEDDED_SIGNUP') {
                    switch (data.event) {
                        case 'FINISH':
                            console.log('Signup finished:', data.data);
                            await this.dotNetRef?.invokeMethodAsync(
                                'OnMetaSignupCompleteEvent',
                                'Finish',
                                data.data.phone_number_id || '',
                                data.data.waba_id || '',
                                data.data.business_id || '',
                                'Signup finished',
                                data.data.current_step || 'unknown',
                                JSON.stringify(data) || ''
                            );
                            break;
                        case 'CANCEL':
                            console.log('Signup cancelled:', data.data.current_step);
                            await this.dotNetRef?.invokeMethodAsync(
                                'OnMetaSignupEvent',
                                'Cancel',
                                '',
                                '',
                                '',
                                `Cancelled at ${data.data.current_step || 'unknown step'}`,
                                data.data.current_step || 'unknown',
                                JSON.stringify(data) || ''
                            );
                            break;
                        case 'ERROR':
                            console.log('Signup error:', data.data.error_message);
                            await this.dotNetRef?.invokeMethodAsync(
                                'OnMetaSignupEvent',
                                'Error',
                                '',
                                '',
                                '',
                                data.data.error_message || 'Unknown error',
                                data.data.current_step || 'unknown',
                                JSON.stringify(data) || ''
                            );
                            break;
                    }
                }
            } catch {
                console.log('Non-JSON response:', event.data);
                await this.dotNetRef?.invokeMethodAsync(
                    'OnMetaSignupEvent',
                    'Unknown',
                    '',
                    '',
                    '',
                    event.data.current_step || 'unknown step',
                    event.data.current_step || 'unknown',
                    JSON.stringify(event) || ''
                );
            }
        });
    }

    private fbLoginCallback(response: FBLoginResponse): void {
        if (response.authResponse?.code) {
            const code = response.authResponse.code;
            console.log('Received code:', code);
            fetch('/auth/facebook', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({code})
            })
                .then(res => res.json())
                .then(data => {
                    console.log('Server response:', data);
                })
                .catch(err => console.error('Error sending code to server:', err));
        } else {
            console.warn('No authorization code received, user may have cancelled.');
        }

        const sdkEl = document.getElementById('sdk-response');
        if (sdkEl) {
            sdkEl.textContent = JSON.stringify(response, null, 2);
        }
    }

    /** Called by FB SDK when ready */
    private setupFBInit(): void {
        window.fbAsyncInit = (): void => {
            window.FB.init({
                appId: this.appId,
                autoLogAppEvents: true,
                xfbml: true,
                version: 'v24.0',
            });
        };
    }

    /** Launch the Meta embedded signup window */
    private defineLaunchMethod(): void {
        window.launchWhatsAppSignup = (): void => {
            window.FB.login(this.fbLoginCallback.bind(this), {
                config_id: this.configId,
                response_type: 'code',
                override_default_response_type: true,
                extras: {version: 'v3'},
            });
        };
    }

    /** Load FB SDK asynchronously */
    private loadFacebookSDK(): void {
        (function (d: Document, s: string, id: string) {
            const fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) return;

            const js = d.createElement(s) as HTMLScriptElement;
            js.id = id;
            js.src = 'https://connect.facebook.net/en_US/sdk.js';
            js.crossOrigin = 'anonymous';
            fjs.parentNode?.insertBefore(js, fjs);
        })(document, 'script', 'facebook-jssdk');
    }
}

(window as any).MetaEmbeddedSignUp = new MetaEmbeddedSignUp();

declare global {
    interface Window {
        fbAsyncInit?: () => void;
        FB: {
            init: (options: {
                appId: string;
                autoLogAppEvents: boolean;
                xfbml: boolean;
                version: string;
            }) => void;
            login: (
                callback: (response: FBLoginResponse) => void,
                options?: {
                    config_id?: string;
                    response_type?: string;
                    override_default_response_type?: boolean;
                    extras?: Record<string, string>;
                }
            ) => void;
        };
        launchWhatsAppSignup: (appId?: string, configId?: string) => void;
    }
}

interface FBAuthResponse {
    code?: string;
}

interface FBLoginResponse {
    authResponse?: FBAuthResponse;

    [key: string]: any;
}

interface WhatsAppSignupData {
    type: string;
    event: 'FINISH' | 'CANCEL' | 'ERROR' | string;
    data: {
        phone_number_id?: string;
        waba_id?: string;
        current_step?: string;
        error_message?: string;
        [key: string]: any;
    };
}
